'use client';

import { Instagram, Linkedin } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { FaGlobe } from 'react-icons/fa';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const colors = {
	primary: '#051449',
	accent: '#FFCF23',
	softBlue: '#D2DFFF',
	softYellow: '#F8ECC1',
};

export default function Footer() {
	const { user } = useAuth();
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<
		'idle' | 'loading' | 'ok' | 'error'
	>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	async function subscribe(e: React.FormEvent) {
		e.preventDefault();
		setErrorMessage('');
		if (!/^\S+@\S+\.\S+$/.test(email)) {
			setErrorMessage('Please enter a valid email.');
			setStatus('error');
			return;
		}
		setStatus('loading');
		try {
			await api.post('/newsletter/subscribe', { email });
			setStatus('ok');
			setEmail('');
		} catch (err: any) {
			console.error(err);
			setErrorMessage(err.response?.data?.message || 'Failed to subscribe. Please try again.');
			setStatus('error');
		}
	}

	return (
		<footer className="bg-white border-t mt-16">
			<div className="max-w-7xl mx-auto px-6 py-12">
				<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
					{/* Brand */}
					<div>
						<div className="flex items-center gap-3 mb-3">
							<Image
								src={'/logo.jpg'}
								alt="Yunimall logo"
								objectFit="cover"
								width={70}
								height={55}
							/>
							<div>
								<p
									className="text-lg font-semibold"
									style={{ color: colors.primary }}
								>
									Yunimall Limited
								</p>
								<p className="text-sm text-gray-500">
									Connect! Buy! Sell!- The Yuni way!
								</p>
							</div>
						</div>

						<p className="text-sm text-gray-600">
							Yunimall connects student businesses with
							fellow students across campuses, making it
							easier to be both a student and an
							entrepreneur.
						</p>

						<div className="flex items-center gap-3 mt-4">
							{/* social icons */}
							<a
								href="https://www.linkedin.com/company/yunimall-limited/"
								aria-label="Linkedin"
								className="inline-flex p-2 cursor-pointer rounded-md hover:bg-gray-100"
								title="Linkedin"
							>
								<Linkedin />
							</a>

							<a
								href="https://instagram.com/yu_nimall"
								aria-label="Instagram"
								className="inline-flex p-2 cursor-pointer rounded-md hover:bg-gray-100"
								title="Instagram"
							>
								<Instagram />
							</a>

							<a
								href="https://yuni-mall.com"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Website"
								className="inline-flex p-2 cursor-pointer rounded-md hover:bg-gray-100"
								title="Website"
							>
								<FaGlobe size={22} />
							</a>
						</div>
					</div>

					{/* Quick links */}
					<div>
						<h3
							className="text-sm font-semibold mb-3"
							style={{ color: colors.primary }}
						>
							Quick links
						</h3>
						<ul className="space-y-2 text-sm text-gray-600">
							<li>
								<Link
									href="/stores"
									className="hover:underline"
								>
									Browse stores
								</Link>
							</li>
							<li>
								<Link
									href="https://yuni-mall.com/about"
									target='_blank'
									className="hover:underline"
								>
									About us
								</Link>
							</li>
							<li>
								<Link
									href="/help-and-support"
									className="hover:underline"
								>
									Help & support
								</Link>
							</li>
							<li>
								<Link
									href="/terms-and-conditions"
									className="hover:underline"
								>
									Terms and conditions
								</Link>
							</li>
							<li>
								<Link
									href="/privacy-policy"
									className="hover:underline"
								>
									Privacy policy
								</Link>
							</li>
						</ul>
					</div>

					{/* For sellers/admin */}
					<div>
						<h3
							className="text-sm font-semibold mb-3"
							style={{ color: colors.primary }}
						>
							For sellers
						</h3>
						<ul className="space-y-2 text-sm text-gray-600">
							<li>
								<Link
									href="/vendor/dashboard"
									className="hover:underline"
								>
									Seller dashboard
								</Link>
							</li>
							{user && (user.role === 'admin' || user.role === 'vendor' || user.role === 'manager') && (
								<li>
									<Link
										href="/pricing"
										className="hover:underline"
									>
										Fees & pricing
									</Link>
								</li>
							)}
							{(user?.role === 'admin' || user?.role === 'manager') && (
								<li>
									<Link
										href="/admin/dashboard"
										className="hover:underline"
									>
										Admin Dashboard
									</Link>
								</li>
							)}
							<li>
								<Link
									href="/guides"
									className="hover:underline"
								>
									Seller guides
								</Link>
							</li>
						</ul>
					</div>

					{/* Newsletter */}
					<div>
						<h3
							className="text-sm font-semibold mb-3"
							style={{ color: colors.primary }}
						>
							Get updates
						</h3>
						<p className="text-sm text-gray-600 mb-3">
							Subscribe for product announcements and seller
							tips.
						</p>

						<form
							onSubmit={subscribe}
							className="flex flex-col gap-2"
						>
							<label
								htmlFor="footer-news"
								className="sr-only"
							>
								Email address
							</label>
							<input
								id="footer-news"
								type="email"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									setStatus('idle');
								}}
								placeholder="you@university.edu"
								className="flex-1 p-3 border rounded-md focus:outline-none"
								style={{ borderColor: colors.softBlue }}
							/>
							<button
								type="submit"
								className="px-4 py-4 rounded-md font-semibold"
								style={{
									background: colors.accent,
									color: colors.primary,
								}}
								disabled={status === 'loading'}
							>
								{status === 'loading'
									? 'Sending...'
									: 'Subscribe'}
							</button>
						</form>

						{status === 'error' && (
							<p className="text-sm text-red-600 mt-2">
								{errorMessage || 'Please enter a valid email or try later.'}
							</p>
						)}
					</div>
				</div>

				{/* bottom */}
				<div className="mt-8 pt-6 border-t flex flex-col md:flex-row items-center justify-between gap-4">
					<p className="text-sm text-gray-500">
						© {new Date().getFullYear()} Yunimall. All
						rights reserved.
					</p>
					<div className="text-sm text-gray-500">
						Built for students • Made with ❤️
					</div>
				</div>
			</div>

			{/* Success Modal */}
			{status === 'ok' && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
					<div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in duration-300">
						<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
							<svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<h3 className="text-xl font-bold text-gray-900 mb-2">Subscribed!</h3>
						<p className="text-gray-600 mb-6">
							Thank you for subscribing to our newsletter. We'll keep you updated with the latest news.
						</p>
						<button
							onClick={() => setStatus('idle')}
							className="w-full py-2.5 px-4 bg-[#051449] text-white font-semibold rounded-lg hover:bg-blue-900 transition-colors"
						>
							Close
						</button>
					</div>
				</div>
			)}
		</footer>
	);
}
